import { createFeatureSelector, createSelector } from '@ngrx/store';
import { smartRecoFeatureKey } from '../smart-reco-feature-key';
import { SmartRecoState } from '../smart-reco.state';
import { CriteriaType } from '@app/shared/utils/enums/criteria-type.enum';

/**
 * Number of products we want to display
 */
const DRY_PRODUCTS_MAX_LENGTH = 3;
const WET_PRODUCTS_MAX_LENGTH = 5;

const selectSmartReco = createFeatureSelector<SmartRecoState>(smartRecoFeatureKey);

export const selectSmartRecoProducts = createSelector(selectSmartReco, (state) =>
  state.recommendation?.products?.slice(0, DRY_PRODUCTS_MAX_LENGTH)
);
/**
 * Emits true if the recommendation is accurate enough,
 * meaning the covergage is good enough
 */
export const selectSmartRecoAccuracy = createSelector(selectSmartRecoProducts, (productsRecommendation) =>
  productsRecommendation?.every((product) => {
    const idealPmeans = 0.65;
    const diseasesCriterias = product?.coverage?.criterias?.filter((criteria) => criteria.type === CriteriaType.Diseases) || [];
    const pmeansCriteriasIsOk = diseasesCriterias.every((diseaseCriteria) => diseaseCriteria.pmean >= idealPmeans);
    const pmeansProductsIsOk = product.coverage.pmean >= idealPmeans;
    return pmeansProductsIsOk && pmeansCriteriasIsOk;
  })
);

/**
 * Selects the wet products related to the selected dry product
 */
export const selectSmartRecoRelatedWetProducts = createSelector(selectSmartReco, (state) =>
  state.relatedWetProducts?.slice(0, WET_PRODUCTS_MAX_LENGTH)
);

export const selectSmartRecoProductsCostPerDay = createSelector(selectSmartReco, (state) => state.productsCostPerDay);

export const selectSmartRecoNotRecommendedCostPerDay = createSelector(selectSmartReco, (state) => state.notRecommendedProductCostPerDay);
/**
 * Selects catalog products
 */
export const selectSmartRecoCatalogProductsAutocomplete = createSelector(selectSmartReco, (state) =>
  state.catalogProducts.map((product) => ({
    label: product.name,
    value: product,
  }))
);
/**
 * Selects not recommended product
 */
export const selectSmartRecoNotRecommendedProduct = createSelector(selectSmartReco, (state) => state.notRecommendedProduct);

export const selectSmartRecoProductRecommendationErrorType = createSelector(selectSmartReco, (state) => state.recommendationErrorType);
export const selectSmartRecoProductRecommendationRetryCount = createSelector(selectSmartReco, (state) => state.retryCount);

export const selectSmartRecoProductRecommendationSessionIdError = createSelector(
  selectSmartReco,
  (state) => state.recommendationSessionIdError
);

/**
 * Selects recommended and not recommended products
 */
export const selectSmartRecoProductsAndNotRecommendedProduct = createSelector(
  selectSmartRecoProducts,
  selectSmartRecoNotRecommendedProduct,
  (recommendedProducts, notRecommendedProduct) =>
    notRecommendedProduct ? [...recommendedProducts, notRecommendedProduct] : recommendedProducts
);
